import {
  Accordion,
  ControlledDropDown,
  ControlledMultiSelect,
  CustomButton,
  DropdownType,
  filterOperatorValues,
  FormInput,
  SvgSearchIcon,
} from '@ui-kit'
import { v4 as uuid } from 'uuid'
import {
  EMAIL_REGEX,
  ENTITY_ID,
  FIELD_VALUE_CONSTANTS,
  GENERATE_CONSENT_NAMESPACES,
  NAMESPACES,
} from '../../utils/constants'
import {
  GenerateConsentFormType,
  GetPersonaDetailsApiResponse,
  ProviderSearchOptionResponse,
} from '../../types/mecConsent.types'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import {
  addToaster,
  fetchConsentExpirationData,
  generateMECConsent,
  getMECConsentsDropdownData,
  getMECPersonaDetails,
  getMECProductData,
  getMECProgramData,
  getMECRecipientEntityTypeData,
  getProviderDropdownOptions,
  hideLoader,
  showLoader,
} from '@app-store'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { CHECK_ELIGIBILITY_LOCATION } from '../../utils/maxRTE'
import { useParams, useSearchParams } from 'react-router-dom'
import { WorkflowProcessType } from '../../types/intakeQueue.types'
import { RECORD_STATUS } from '../../constants/form.constants'
import axios from 'axios'

const GenerateEConsentModalContent = ({
  closeGenerateConsentModal,
  patientKey,
  patientId,
  from,
  productId,
  caseID,
  workflowDef,
  caseProviderKey,
}: {
  closeGenerateConsentModal: () => void
  patientKey: string | null
  patientId: number | null
  productId: number | null
  caseID: string | undefined
  from: string
  workflowDef?: WorkflowProcessType | null
  caseProviderKey?: string | null
}) => {
  const dispatch = useAppDispatch()
  const { caseId } = useParams()
  const [searchParams] = useSearchParams()
  const case_id = searchParams.get('caseId')
  const caseData = useAppSelector((state) => state?.workflowData?.caseData)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const generateConsentRef: GenerateConsentFormType = {
    program: '',
    product: '',
    recipient_entity_type: '',
    request_channel: '',
    consent_type: [],
    patient_first_name: '',
    patient_last_name: '',
    patient_dob: '',
    patient_email_address: '',
    patient_representative_contact: '',
    patient_representative_contact_first_name: '',
    patient_representative_contact_last_name: '',
    patient_representative_contact_email: '',
    patient_representative_contact_dob: '',
    provider_search_key: '',
    provider_first_name: '',
    provider_last_name: '',
    provider_email: '',
    provider_npi: '',
    provider_dob: '',
  }
  const {
    register,
    watch,
    formState: { errors },
    control,
    setValue,
    trigger,
    getValues,
  } = useForm({ defaultValues: generateConsentRef })
  const productField = watch('product')
  const programField = watch('program')
  const recipientEntityTypeField = watch('recipient_entity_type')
  const [programOptions, setProgramOptions] = useState<DropdownType<string>[]>(
    []
  )
  const [productOptions, setProductOptions] = useState<DropdownType<string>[]>(
    []
  )
  const [consentDropdownOptions, setConsentDropdownOptions] = useState<
    DropdownType<string>[]
  >([])

  const [providerDropdownOptions, setProviderDropdownOptions] =
    useState<DropdownType<string>[]>()
  const [defaultProviderDropdownOptions, setDefaultProviderDropdownOptions] =
    useState<DropdownType<string>[]>([])
  const [requestChannelOptions, setRequestChannelOptions] = useState<
    DropdownType<string>[]
  >([])
  const [patientRepRelationshipOptions, setPatientRepRelationshipOptions] =
    useState<DropdownType<string>[]>([])
  const [recipientEntityTypeOptions, setRecipientEntityTypeOptions] = useState<
    DropdownType<string>[]
  >([])
  const [requestorId, setRequestorId] = useState<number | null>(null)
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null)
  const [consentPortalId, setSelectedConsentPortalId] = useState<string | null>(
    null
  )
  const [isProviderDetailsSearched, setIsProviderDetailsSearched] =
    useState<boolean>(false)
  const handleMultiSelectDropdownChange = (
    e: DropdownType<string>[],
    key: keyof GenerateConsentFormType
  ) => {
    const values = e?.map((item) => item?.value)
    setValue(key, values, { shouldValidate: true })
  }
  const fetchProductData = async (programId: string) => {
    try {
      dispatch(showLoader())
      const response = await getMECProductData(programId)
      if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
        const productOptions = response?.data?.map(
          (item: {
            id: number
            product_uuid: string
            trademarked_name: string
          }) => ({
            label: item?.trademarked_name ?? '',
            value: item?.id?.toString(),
          })
        )
        setProductOptions(productOptions)
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchConsentDropdownData = async (
    portalId: string,
    productId: string
  ) => {
    try {
      dispatch(showLoader())
      const response = await getMECConsentsDropdownData(
        portalId,
        productId,
        recipientEntityTypeField
      )
      if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
        const consentDropdownOptions = response?.data?.map(
          (item: { id: number; name: string }) => ({
            label: item?.name ?? '',
            value: item?.id?.toString() ?? '',
          })
        )
        setConsentDropdownOptions(consentDropdownOptions)
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchRecipientEntityTypeData = async (programId: string) => {
    try {
      dispatch(showLoader())
      const response = await getMECRecipientEntityTypeData(programId)
      if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
        const consentPortalId = response?.data?.consent_portal_id
        setSelectedConsentPortalId(consentPortalId ?? '')
        const recipientEntityTypeOptions =
          response?.data?.consent_portal_personas?.map(
            (item: { id: number; persona_name: string }) => ({
              label: item?.persona_name ?? '',
              value: item?.id?.toString(),
            })
          )
        setRecipientEntityTypeOptions(recipientEntityTypeOptions)
      }
    } catch (error) {
      console.error(error)
      dispatch(
        addToaster({
          message: axios.isAxiosError(error)
            ? error.response?.data?.message || error?.message
            : GENERATE_CONSENT_NAMESPACES.PERSONA_ERROR,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchPersonaDetails = async (
    patientKey: string | null,
    personaName: string,
    provider_id?: string
  ) => {
    const normalizedProviderId = provider_id?.trim()

    if (
      personaName === GENERATE_CONSENT_NAMESPACES.PROVIDER &&
      !normalizedProviderId
    ) {
      setRequestorId(null)
      setSelectedPersona(personaName)
      setIsProviderDetailsSearched(false)
      return
    }

    try {
      setSelectedPersona(personaName)
      dispatch(showLoader())
      const response = await getMECPersonaDetails({
        requestor_key: patientKey ?? '',
        requested_persona: personaName,
        requested_persona_key: normalizedProviderId || null,
      })
      if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
        const data: GetPersonaDetailsApiResponse = response?.data
        const patient_details = data?.patient_details
        const provider_details = data?.provider_details
        const patient_rep_details = data?.patient_rep_details
        if (personaName === GENERATE_CONSENT_NAMESPACES.PATIENT)
          setRequestorId(null)
        else if (personaName === GENERATE_CONSENT_NAMESPACES.PROVIDER)
          setRequestorId(Number(provider_details?.id))
        if (patient_details?.first_name)
          setValue('patient_first_name', patient_details?.first_name ?? '', {
            shouldValidate: true,
          })
        else
          setValue('patient_first_name', '', {
            shouldValidate: true,
          })
        if (patient_details?.last_name)
          setValue('patient_last_name', patient_details?.last_name ?? '', {
            shouldValidate: true,
          })
        else
          setValue('patient_last_name', '', {
            shouldValidate: true,
          })
        if (patient_details?.email)
          setValue('patient_email_address', patient_details?.email ?? '', {
            shouldValidate: true,
          })
        else
          setValue('patient_email_address', '', {
            shouldValidate: true,
          })
        if (patient_details?.dob)
          setValue('patient_dob', patient_details?.dob ?? '', {
            shouldValidate: true,
          })
        else
          setValue('patient_dob', '', {
            shouldValidate: true,
          })
        if (provider_details?.first_name)
          setValue('provider_first_name', provider_details?.first_name ?? '', {
            shouldValidate: true,
          })
        else
          setValue('provider_first_name', '', {
            shouldValidate: true,
          })
        if (provider_details?.last_name)
          setValue('provider_last_name', provider_details?.last_name ?? '', {
            shouldValidate: true,
          })
        else
          setValue('provider_last_name', '', {
            shouldValidate: true,
          })
        if (provider_details?.email)
          setValue('provider_email', provider_details?.email ?? '', {
            shouldValidate: true,
          })
        else
          setValue('provider_email', '', {
            shouldValidate: true,
          })
        if (provider_details?.dob)
          setValue('provider_dob', provider_details?.dob ?? '', {
            shouldValidate: true,
          })
        else
          setValue('provider_dob', '', {
            shouldValidate: true,
          })
        if (provider_details?.npi)
          setValue('provider_npi', provider_details?.npi ?? '', {
            shouldValidate: true,
          })
        else
          setValue('provider_npi', '', {
            shouldValidate: true,
          })
        const patientRepRelationshipOptions = (patient_rep_details ?? []).map(
          (item) => ({
            label: item?.relationship?.toString() ?? '',
            value: item?.id?.toString() ?? '',
            first_name: item?.first_name ?? '',
            last_name: item?.last_name ?? '',
            dob: item?.dob ?? '',
            email: item?.email ?? '',
          })
        )
        setPatientRepRelationshipOptions(patientRepRelationshipOptions)

        if (
          personaName === GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE
        ) {
          const selectedRep = patientRepRelationshipOptions?.find(
            (item) =>
              item?.value ===
              (watch('patient_representative_contact')?.toString() ?? '')
          )
          setRequestorId(selectedRep ? Number(selectedRep?.value) : null)
          if (!selectedRep) {
            setValue('patient_representative_contact', '', {
              shouldValidate: true,
            })
            setValue('patient_representative_contact_first_name', '', {
              shouldValidate: true,
            })
            setValue('patient_representative_contact_last_name', '', {
              shouldValidate: true,
            })
            setValue('patient_representative_contact_dob', '', {
              shouldValidate: true,
            })
            setValue('patient_representative_contact_email', '', {
              shouldValidate: true,
            })
          }
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleDropdownChange = (
    e: DropdownType<unknown, string>,
    key: keyof GenerateConsentFormType
  ) => {
    if (key === 'recipient_entity_type') {
      setValue('patient_dob', '')
      setValue('patient_email_address', '')
      setValue('patient_first_name', '')
      setValue('patient_last_name', '')
      setValue('consent_type', [])
      setIsProviderDetailsSearched(false)
      setRequestorId(null)
      const selectedPersonaLabel =
        recipientEntityTypeOptions?.find(
          (item) => item?.value?.toString() === e?.value?.toString()
        )?.label ?? ''

      if (selectedPersonaLabel !== GENERATE_CONSENT_NAMESPACES.PROVIDER) {
        fetchPersonaDetails(patientKey, selectedPersonaLabel)
      } else {
        if (!providerDropdownOptions || providerDropdownOptions.length === 0) {
          if (
            defaultProviderDropdownOptions &&
            defaultProviderDropdownOptions.length
          ) {
            setProviderDropdownOptions(defaultProviderDropdownOptions)
          } else {
            handleProviderSearchChange('')
          }
        }
        const existingProviderKey =
          watch('provider_search_key')?.toString() ?? ''
        if (existingProviderKey) {
          fetchPersonaDetails(
            patientKey,
            GENERATE_CONSENT_NAMESPACES.PROVIDER,
            existingProviderKey
          )
          setIsProviderDetailsSearched(true)
          handleProviderSearchChange(existingProviderKey)
        } else if (
          (from === CHECK_ELIGIBILITY_LOCATION.CASE ||
            from === CHECK_ELIGIBILITY_LOCATION.INTAKE) &&
          caseProviderKey
        ) {
          setValue('provider_search_key', caseProviderKey, {
            shouldValidate: true,
          })
          fetchPersonaDetails(
            patientKey,
            GENERATE_CONSENT_NAMESPACES.PROVIDER,
            caseProviderKey
          )
          setIsProviderDetailsSearched(true)
          handleProviderSearchChange(caseProviderKey)
        }
      }
    }
    if (key === 'patient_representative_contact') {
      const data = e as {
        label: string
        value: string
        first_name: string
        last_name: string
        dob: string
        email: string
      }
      setRequestorId(Number(data?.value ?? ''))
      setValue(
        'patient_representative_contact_first_name',
        data?.first_name ?? '',
        { shouldValidate: true }
      )
      setValue(
        'patient_representative_contact_last_name',
        data?.last_name ?? '',
        { shouldValidate: true }
      )
      setValue('patient_representative_contact_dob', data?.dob ?? '', {
        shouldValidate: true,
      })
      setValue('patient_representative_contact_email', data?.email ?? '', {
        shouldValidate: true,
      })
    }
    if (key === 'program') {
      fetchProductData(e?.value?.toString())
      fetchRecipientEntityTypeData(e?.value?.toString())
    }
    if (key === 'provider_search_key') {
      const providerKey = e?.value?.toString() ?? ''
      setIsProviderDetailsSearched(false)
      setValue('provider_email', '')
      setValue('provider_npi', '')
      setValue('provider_first_name', '')
      setValue('provider_last_name', '')
      setValue('provider_dob', '')
      setValue('patient_dob', '')
      setValue('patient_email_address', '')
      setValue('patient_first_name', '')
      setValue('patient_last_name', '')

      let skipProviderFetch = false

      if (!providerKey) {
        setValue(key, '', { shouldValidate: true })
        if (
          defaultProviderDropdownOptions &&
          defaultProviderDropdownOptions.length
        ) {
          setProviderDropdownOptions(defaultProviderDropdownOptions)
        } else {
          handleProviderSearchChange('')
        }
        setIsProviderDetailsSearched(false)
        setRequestorId(null)
        skipProviderFetch = true
      }

      const personaName =
        recipientEntityTypeOptions?.find(
          (item) =>
            item?.value?.toString() ===
            watch('recipient_entity_type')?.toString()
        )?.label ?? ''

      if (!skipProviderFetch) {
        fetchPersonaDetails(patientKey, personaName, providerKey)
        setIsProviderDetailsSearched(true)
        handleProviderSearchChange(providerKey)
      }
    }
    setValue(key, e?.value, { shouldValidate: true })
  }
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof GenerateConsentFormType
  ) => {
    if (key === 'provider_search_key' && !e?.target?.value?.length) {
      setIsProviderDetailsSearched(false)
      setValue('provider_email', '')
      setValue('provider_npi', '')
      setValue('provider_first_name', '')
      setValue('provider_last_name', '')
      setValue('provider_dob', '')
      setValue('patient_dob', '')
      setValue('patient_email_address', '')
      setValue('patient_first_name', '')
      setValue('patient_last_name', '')
      setRequestorId(null)
    }
    setValue(key, e?.target?.value, { shouldValidate: true })
  }
  const getEmail = (persona: string) => {
    switch (persona) {
      case GENERATE_CONSENT_NAMESPACES.PATIENT:
        return watch('patient_email_address')
      case GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE:
        return watch('patient_representative_contact_email')
      case GENERATE_CONSENT_NAMESPACES.PROVIDER:
        return watch('provider_email')
      default:
        return ''
    }
  }
  const generateMECPortalConsent = async (payload: {
    consent_portal_id: number
    program_id: number
    product_id: number
    consent_persona_id: number
    request_channel_id: number
    email: string
    case_id: number | null
    persona_details: {
      patient_id: number
      requestor_id: number | null
    }
    consents: number[]
  }) => {
    try {
      dispatch(showLoader())
      const response = await generateMECConsent(payload)
      if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
        dispatch(
          addToaster({
            message: GENERATE_CONSENT_NAMESPACES?.GENERATE_CONSENT_SUCCESS_MSG,
            type: 'success',
            id: uuid(),
          })
        )
      }
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          message: axios.isAxiosError(err)
            ? err.response?.data?.message || err?.message
            : GENERATE_CONSENT_NAMESPACES?.GENERATE_CONSENT_ERROR_MSG,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleGenerate = async () => {
    const isValid = await trigger()
    if (isValid) {
      const values = getValues()
      const selectedRequestChannelLabel =
        requestChannelOptions?.find(
          (item) =>
            item?.value?.toString() === values?.request_channel?.toString()
        )?.label ?? ''
      const isEmailChannel =
        selectedRequestChannelLabel?.toLowerCase() ===
        GENERATE_CONSENT_NAMESPACES.EMAIL.toLowerCase()
      if (isEmailChannel) {
        if (!getEmail(String(selectedPersona))?.length) {
          dispatch(
            addToaster({
              message: GENERATE_CONSENT_NAMESPACES?.EMAIL_ADDRESS_IS_REQUIRED,
              type: 'error',
              id: uuid(),
            })
          )
          return
        }
        if (!EMAIL_REGEX.test(getEmail(String(selectedPersona)))) {
          dispatch(
            addToaster({
              message: GENERATE_CONSENT_NAMESPACES?.INVALID_EMAIL_PERSONA_MSG,
              type: 'error',
              id: uuid(),
            })
          )
          return
        }
      }

      const payload: {
        consent_portal_id: number
        program_id: number
        product_id: number
        consent_persona_id: number
        request_channel_id: number
        email: string
        case_id: number | null
        persona_details: {
          patient_id: number
          requestor_id: number | null
        }
        consents: number[]
      } = {
        consent_portal_id: Number(consentPortalId ?? ''),
        program_id: Number(values?.program ?? ''),
        product_id: Number(values?.product ?? ''),
        consent_persona_id: Number(values?.recipient_entity_type ?? ''),
        request_channel_id: Number(values?.request_channel ?? ''),
        email: getEmail(String(selectedPersona)),
        case_id: Number(caseId ?? case_id ?? caseID ?? '')
          ? Number(caseId ?? case_id ?? caseID ?? '')
          : null,
        persona_details: {
          patient_id: Number(patientId),
          requestor_id: requestorId,
        },
        consents: values?.consent_type?.map((item) => parseInt(item)),
      }
      generateMECPortalConsent(payload)
    }
  }

  const handleProviderSearchChange = (searchText?: string) => {
    fetchSearchedOptions(searchText)
  }

  useEffect(() => {
    if (productField && programField && recipientEntityTypeField) {
      fetchConsentDropdownData(consentPortalId ?? '', productField)
    }
  }, [productField, programField, recipientEntityTypeField])
  useEffect(() => {
    if (
      programOptions?.length &&
      (from === CHECK_ELIGIBILITY_LOCATION.CASE ||
        from === CHECK_ELIGIBILITY_LOCATION.INTAKE)
    ) {
      const programId =
        caseData?.programs?.id?.toString() ??
        workflowDefinition?.program_id?.toString() ??
        workflowDef?.program_id?.toString() ??
        ''
      setValue('program', programId, { shouldValidate: true })
      fetchProductData(programId)
      fetchRecipientEntityTypeData(programId)
    }
  }, [programOptions])
  useEffect(() => {
    if (
      productOptions?.length &&
      (from === CHECK_ELIGIBILITY_LOCATION.CASE ||
        from === CHECK_ELIGIBILITY_LOCATION.INTAKE)
    ) {
      const prodId =
        caseData?.products?.id?.toString() ?? productId?.toString() ?? ''
      setValue('product', prodId, { shouldValidate: true })
    }
  }, [productOptions])

  useEffect(() => {
    if (requestChannelOptions?.length)
      setValue('request_channel', requestChannelOptions?.[0]?.value ?? '', {
        shouldValidate: true,
      })
  }, [requestChannelOptions])

  const fetchSearchedOptions = async (searchText?: string) => {
    try {
      const criteria = searchText?.length
        ? [
            {
              field_name: 'provider_key',
              operation: 'LIKE',
              preceding_condition: 'OR',
              values: [searchText],
            },
            {
              field_name: 'first_name',
              operation: 'LIKE',
              preceding_condition: 'OR',
              values: [searchText],
            },
            {
              field_name: 'last_name',
              operation: 'LIKE',
              preceding_condition: 'OR',
              values: [searchText],
            },
            {
              field_name: 'npi',
              operation: 'LIKE',
              preceding_condition: 'OR',
              values: [searchText],
            },
          ]
        : []
      dispatch(showLoader())
      const res = await getProviderDropdownOptions({
        entity_id: ENTITY_ID.PROVIDER,
        select: ['last_name', 'provider_key', 'first_name', 'npi'],
        filters: [
          {
            criteria: criteria,
          },
          {
            criteria: [
              {
                field_name: 'record_status',
                operation: filterOperatorValues.EQUALS,
                values: [RECORD_STATUS.ACTIVE],
              },
            ],
          },
        ],
      })
      const response = res?.map((item: ProviderSearchOptionResponse) => ({
        label: `${item?.provider_key ?? '--'} | ${item?.first_name ?? '--'} | ${
          item?.last_name ?? '--'
        } | ${item?.npi ? ` ${item?.npi}` : '--'}`,
        value: item?.provider_key ?? '',
      }))
      setProviderDropdownOptions(response)
      if (!searchText) setDefaultProviderDropdownOptions(response)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  useEffect(() => {
    const fetchProgramData = async () => {
      try {
        dispatch(showLoader())
        const response = await getMECProgramData()
        if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
          const programOptions = response?.data?.map(
            (item: {
              id: number
              work_program_id: string
              operational_program_name: string
            }) => ({
              label: item?.operational_program_name ?? '',
              value: item?.id?.toString(),
            })
          )
          setProgramOptions(programOptions)
        }
      } catch (error) {
        console.error(error)
      } finally {
        dispatch(hideLoader())
      }
    }
    const fetchRequestChannelDropdownData = async () => {
      try {
        dispatch(showLoader())
        const response = await fetchConsentExpirationData('mec_request_channel')
        if (response?.status === FIELD_VALUE_CONSTANTS.TWO_HUNDRED) {
          const requestChannelOptions = response?.data?.map(
            (item: { record_status: string; value: string; id: number }) => ({
              label: item?.value ?? '',
              value: item?.id?.toString() ?? '',
            })
          )
          setRequestChannelOptions(requestChannelOptions)
        }
      } catch (error) {
        console.error(error)
      } finally {
        dispatch(hideLoader())
      }
    }
    fetchProgramData()
    fetchRequestChannelDropdownData()
    fetchSearchedOptions()
  }, [])

  return (
    <form className="p-4 flex flex-col gap-4 overflow-y-auto">
      <div className="flex w-full items-center">
        <p className="font-semibold font-primary">
          {NAMESPACES.PATIENT_KEY} | {NAMESPACES.EDIT} /
          <span className="text-primary-500 text-lg">
            {NAMESPACES.GENERATE_CONSENT}
          </span>
        </p>
        <div className="flex gap-2 ml-auto items-center justify-center">
          <CustomButton
            type="button"
            isFilled={true}
            horizontalPaddingClasses="px-[1.125rem]"
            height="h-8"
            onClick={handleGenerate}
            dataQaId="patient-layout-add-btn"
          >
            {NAMESPACES.GENERATE}
          </CustomButton>
          <div className="bg-accent-800 h-6 w-[0.05rem]"></div>
          <CustomButton
            type="button"
            isFilled={false}
            height="h-8"
            horizontalPaddingClasses="px-[1.125rem]"
            onClick={closeGenerateConsentModal}
            dataQaId="patient-layout-add-btn"
          >
            {NAMESPACES.CLOSE}
          </CustomButton>
        </div>
      </div>
      <div>
        <Accordion
          className="border-primary-450"
          isDefaultActive={true}
          heading={
            <div className="font-semibold">
              {NAMESPACES.PATIENT_KEY} {NAMESPACES.CONSENT}
            </div>
          }
        >
          <div className="flex flex-col gap-4 p-4">
            {/* Common fields */}
            <div className="flex gap-2 justify-between">
              <ControlledDropDown
                dataQaId="program-dropdown"
                control={control}
                label={GENERATE_CONSENT_NAMESPACES.PROGRAM}
                isSearchable={false}
                required
                options={programOptions}
                disabled={
                  from === CHECK_ELIGIBILITY_LOCATION.CASE ||
                  from === CHECK_ELIGIBILITY_LOCATION.INTAKE
                }
                isCrossable={false}
                name="program"
                errors={errors}
                onChange={(e) => handleDropdownChange(e, 'program')}
              />
              <ControlledDropDown
                dataQaId="product-dropdown"
                control={control}
                required
                label={GENERATE_CONSENT_NAMESPACES.PRODUCT}
                isSearchable={false}
                options={productOptions}
                disabled={
                  !watch('program') ||
                  from === CHECK_ELIGIBILITY_LOCATION.CASE ||
                  from === CHECK_ELIGIBILITY_LOCATION.INTAKE
                }
                isCrossable={false}
                name="product"
                errors={errors}
                onChange={(e) => handleDropdownChange(e, 'product')}
              />
              <ControlledDropDown
                dataQaId="recipient-entity-type-dropdown"
                control={control}
                required
                label={GENERATE_CONSENT_NAMESPACES.RECIPIENT_ENTITY_TYPE}
                isSearchable={false}
                options={recipientEntityTypeOptions ?? []}
                isCrossable={false}
                disabled={!watch('program')}
                name="recipient_entity_type"
                errors={errors}
                onChange={(e) =>
                  handleDropdownChange(e, 'recipient_entity_type')
                }
              />
              <ControlledDropDown
                dataQaId="request-channel-dropdown"
                control={control}
                required
                label={GENERATE_CONSENT_NAMESPACES.REQUEST_CHANNEL}
                isSearchable={false}
                options={requestChannelOptions ?? []}
                isCrossable={false}
                name="request_channel"
                errors={errors}
                onChange={(e) => handleDropdownChange(e, 'request_channel')}
              />
            </div>
            <div className="flex">
              <ControlledMultiSelect
                dataQaId="consent-type-dropdown"
                options={consentDropdownOptions ?? ''}
                required
                name="consent_type"
                label={GENERATE_CONSENT_NAMESPACES.CONSENT_TYPE}
                onChange={(e) =>
                  handleMultiSelectDropdownChange(
                    e as DropdownType<string>[],
                    'consent_type'
                  )
                }
                disabled={
                  !(
                    watch('program') &&
                    watch('product') &&
                    watch('recipient_entity_type')
                  )
                }
                noOfChips={2}
                errors={errors}
                control={control}
              />
            </div>

            {/* Patient Representative Fields */}
            {recipientEntityTypeOptions?.find(
              (item) =>
                item?.value?.toString() ===
                watch('recipient_entity_type')?.toString()
            )?.label === GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE && (
              <>
                <div className="flex gap-2 justify-between">
                  <ControlledDropDown
                    dataQaId="patient-representative-contact-dropdown"
                    control={control}
                    label={
                      GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_CONTACT
                    }
                    isSearchable={false}
                    options={patientRepRelationshipOptions ?? []}
                    isCrossable={false}
                    name="patient_representative_contact"
                    errors={errors}
                    onChange={(e) =>
                      handleDropdownChange(e, 'patient_representative_contact')
                    }
                  />
                  {watch('patient_representative_contact') ? (
                    <>
                      <FormInput
                        dataQaId="patient-representative-first-name"
                        errors={errors}
                        register={register}
                        label={
                          GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_FIRST_NAME
                        }
                        name={'patient_representative_contact_first_name'}
                        onChange={(e) =>
                          handleInputChange(
                            e,
                            'patient_representative_contact_first_name'
                          )
                        }
                        disabled
                      />
                      <FormInput
                        dataQaId="patient-representative-last-name"
                        errors={errors}
                        register={register}
                        label={
                          GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_LAST_NAME
                        }
                        name={'patient_representative_contact_last_name'}
                        onChange={(e) =>
                          handleInputChange(
                            e,
                            'patient_representative_contact_last_name'
                          )
                        }
                        disabled
                      />
                      <FormInput
                        dataQaId="patient-representative-dob"
                        errors={errors}
                        type="date"
                        register={register}
                        label={
                          GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_DOB
                        }
                        name={'patient_representative_contact_dob'}
                        onChange={(e) =>
                          handleInputChange(
                            e,
                            'patient_representative_contact_dob'
                          )
                        }
                        disabled
                      />
                    </>
                  ) : null}
                </div>
                {watch('patient_representative_contact') ? (
                  <div className="flex gap-2 justify-between">
                    <FormInput
                      dataQaId="patient-representative-email-input"
                      errors={errors}
                      register={register}
                      label={
                        GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_EMAIL
                      }
                      name={'patient_representative_contact_email'}
                      onChange={(e) =>
                        handleInputChange(
                          e,
                          'patient_representative_contact_email'
                        )
                      }
                      disabled
                      validation={{
                        required: true,
                        pattern: {
                          value: EMAIL_REGEX,
                          message:
                            GENERATE_CONSENT_NAMESPACES.INVALID_EMAIL_MSG,
                        },
                      }}
                    />
                    {/* <ControlledDropDown
                      dataQaId="patient-representative-email-dropdown"
                      control={control}
                      label={
                        GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE_EMAIL
                      }
                      isSearchable={false}
                      // disabled
                      options={[
                        {
                          label: 'Patient Rep Email 1',
                          value: '1',
                        },
                      ]}
                      isCrossable={false}
                      name="patient_representative_contact_email"
                      errors={errors}
                      onChange={(e) =>
                        handleDropdownChange(
                          e,
                          'patient_representative_contact_email'
                        )
                      }
                    /> */}
                  </div>
                ) : null}
              </>
            )}
            {/* Provider Fields */}
            {recipientEntityTypeOptions?.find(
              (item) =>
                item?.value?.toString() ===
                watch('recipient_entity_type')?.toString()
            )?.label === GENERATE_CONSENT_NAMESPACES.PROVIDER && (
              <>
                <div className="flex gap-2 justify-between">
                  <ControlledDropDown
                    dataQaId="provider-id-dropdown"
                    name={'provider_search_key'}
                    errors={errors}
                    label={GENERATE_CONSENT_NAMESPACES.PROVIDER_ID}
                    control={control}
                    isSearchable={!watch('provider_search_key')?.length}
                    isCustomSearch
                    onSearchChange={handleProviderSearchChange}
                    onChange={(e: DropdownType<string>) => {
                      handleDropdownChange(e, 'provider_search_key')
                    }}
                    icon={(_disabled, _closeOptions) => {
                      return watch('provider_search_key')?.length ? (
                        <></>
                      ) : (
                        <SvgSearchIcon pathClass="fill-secondary-500" />
                      )
                    }}
                    options={providerDropdownOptions ?? []}
                    isCrossable
                  />
                  {watch('provider_search_key') && isProviderDetailsSearched ? (
                    <>
                      <FormInput
                        dataQaId="provider-first-name"
                        errors={errors}
                        register={register}
                        label={GENERATE_CONSENT_NAMESPACES.PROVIDER_FIRST_NAME}
                        name={'provider_first_name'}
                        onChange={(e) =>
                          handleInputChange(e, 'provider_first_name')
                        }
                        disabled
                      />
                      <FormInput
                        dataQaId="provider-last-name"
                        errors={errors}
                        register={register}
                        label={GENERATE_CONSENT_NAMESPACES.PROVIDER_LAST_NAME}
                        name={'provider_last_name'}
                        onChange={(e) =>
                          handleInputChange(e, 'provider_last_name')
                        }
                        disabled
                      />
                      <FormInput
                        dataQaId="provider-dob"
                        errors={errors}
                        type="date"
                        register={register}
                        label={GENERATE_CONSENT_NAMESPACES.PROVIDER_DOB}
                        name={'provider_dob'}
                        onChange={(e) => handleInputChange(e, 'provider_dob')}
                        disabled
                      />
                    </>
                  ) : null}
                </div>
                {watch('provider_search_key') && isProviderDetailsSearched ? (
                  <div className="flex gap-2 justify-between">
                    <FormInput
                      dataQaId="provider-npi"
                      errors={errors}
                      register={register}
                      label={GENERATE_CONSENT_NAMESPACES.PROVIDER_NPI}
                      name={'provider_npi'}
                      onChange={(e) => handleInputChange(e, 'provider_npi')}
                      disabled
                    />
                    <FormInput
                      dataQaId="provider-email-input"
                      errors={errors}
                      register={register}
                      label={GENERATE_CONSENT_NAMESPACES.PROVIDER_EMAIL}
                      name={'provider_email'}
                      validation={{
                        required: true,
                        pattern: {
                          value: EMAIL_REGEX,
                          message:
                            GENERATE_CONSENT_NAMESPACES.INVALID_EMAIL_MSG,
                        },
                      }}
                      disabled
                      onChange={(e) => handleInputChange(e, 'provider_email')}
                    />
                    {/* <ControlledDropDown
                      dataQaId="provider-email-dropdown"
                      control={control}
                      label={GENERATE_CONSENT_NAMESPACES.PROVIDER_EMAIL}
                      isSearchable={false}
                      // disabled
                      options={[
                        {
                          label: 'Provider Email 1',
                          value: '1',
                        },
                      ]}
                      isCrossable={false}
                      name="provider_email"
                      errors={errors}
                      onChange={(e) =>
                        handleDropdownChange(e, 'provider_email')
                      }
                    /> */}
                  </div>
                ) : null}
              </>
            )}
            {/* Patient Fields */}
            {recipientEntityTypeOptions?.find(
              (item) =>
                item?.value?.toString() ===
                watch('recipient_entity_type')?.toString()
            )?.label === GENERATE_CONSENT_NAMESPACES.PATIENT ||
            (recipientEntityTypeOptions?.find(
              (item) =>
                item?.value?.toString() ===
                watch('recipient_entity_type')?.toString()
            )?.label === GENERATE_CONSENT_NAMESPACES.PATIENT_REPRESENTATIVE &&
              watch('patient_representative_contact')) ||
            (recipientEntityTypeOptions?.find(
              (item) =>
                item?.value?.toString() ===
                watch('recipient_entity_type')?.toString()
            )?.label === GENERATE_CONSENT_NAMESPACES.PROVIDER &&
              watch('provider_search_key') &&
              isProviderDetailsSearched) ? (
              <>
                <div className="flex gap-2 justify-between">
                  <FormInput
                    dataQaId="patient-first-name"
                    errors={errors}
                    register={register}
                    label={GENERATE_CONSENT_NAMESPACES.PATIENT_FIRST_NAME}
                    name={'patient_first_name'}
                    onChange={(e) => handleInputChange(e, 'patient_first_name')}
                    disabled
                  />
                  <FormInput
                    dataQaId="patient-last-name"
                    errors={errors}
                    register={register}
                    label={GENERATE_CONSENT_NAMESPACES.PATIENT_LAST_NAME}
                    name={'patient_last_name'}
                    onChange={(e) => handleInputChange(e, 'patient_last_name')}
                    disabled
                  />
                  <FormInput
                    dataQaId="patient-dob"
                    errors={errors}
                    type="date"
                    register={register}
                    label={GENERATE_CONSENT_NAMESPACES.PATIENT_DOB}
                    name={'patient_dob'}
                    onChange={(e) => handleInputChange(e, 'patient_dob')}
                    disabled
                  />
                </div>
                <div className="flex gap-2 justify-between">
                  <FormInput
                    dataQaId="patient-email-input"
                    errors={errors}
                    register={register}
                    label={GENERATE_CONSENT_NAMESPACES.PATIENT_EMAIL}
                    name={'patient_email_address'}
                    onChange={(e) =>
                      handleInputChange(e, 'patient_email_address')
                    }
                    disabled
                    validation={{
                      required: true,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: GENERATE_CONSENT_NAMESPACES.INVALID_EMAIL_MSG,
                      },
                    }}
                  />
                  {/* <ControlledDropDown
                    dataQaId="patient-email-dropdown"
                    control={control}
                    label={GENERATE_CONSENT_NAMESPACES.PATIENT_EMAIL}
                    isSearchable={false}
                    // disabled
                    options={[
                      {
                        label: 'Patient Email 1',
                        value: '1',
                      },
                    ]}
                    isCrossable={false}
                    name="patient_email_address"
                    errors={errors}
                    onChange={(e) =>
                      handleDropdownChange(e, 'patient_email_address')
                    }
                  /> */}
                </div>
              </>
            ) : null}
          </div>
        </Accordion>
      </div>
    </form>
  )
}

export default GenerateEConsentModalContent
